import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';

import { SecureUnlockModal } from '@/components/secure/secure-unlock-modal';
import { Text } from '@/components/ui';
import { useSecureUnlock } from '@/services/secure-unlock-context';
import { isEncryptedBlob } from '@/utils/secure-crypto';

interface Props {
	/** Raw stored value — may be plaintext, `sf1:` ciphertext, or already server-masked. */
	value: string | null | undefined;
	/** Plaintext-safe companion mask (e.g. `***-**-1234`) stored alongside the ciphertext. */
	mask?: string | null;
	/** Override the placeholder shown when value is empty. Defaults to "—". */
	emptyPlaceholder?: string;
}

const MONO_FONT = Platform.select({ web: 'ui-monospace, Menlo, monospace', default: 'Courier' });

const maskPlaintext = (value: string): string => {
	const clean = value.replace(/[^0-9A-Za-z]/g, '');
	if (clean.length <= 4) return `••••${clean}`;
	return `••••${clean.slice(-4)}`;
};

/** Clipboard is only wired for web (matches the rest of the app). Gate the copy
 *  affordance on actual availability so it never shows a dead button on native. */
const canCopy = (): boolean =>
	Platform.OS === 'web' && typeof navigator !== 'undefined' && !!navigator.clipboard;

const writeClipboard = async (text: string): Promise<boolean> => {
	try {
		await navigator.clipboard.writeText(text);
		return true;
	} catch {
		return false;
	}
};

/**
 * Render a Tax ID / EIN masked by default. Reveal on hover (web) or tap (web + native).
 *
 * Ciphertext-aware: if `value` is an `sf1:` blob, the reveal action decrypts via
 * the secure-unlock context (or prompts to unlock when locked). The raw ciphertext
 * is NEVER exposed to the renderer — that would leak the encrypted payload through
 * tooltips, screenshots, or copy-paste.
 *
 * Click-to-copy: once the value is revealed AND we hold real plaintext (decrypted
 * or legacy plaintext), pressing copies that plaintext to the clipboard (web only).
 * Only the decrypted plaintext is ever copied — never the mask or the ciphertext.
 */
export const MaskedTaxId: React.FC<Props> = ({ value, mask, emptyPlaceholder = '—' }) => {
	const [revealed, setRevealed] = useState(false);
	const [unlockOpen, setUnlockOpen] = useState(false);
	const [copied, setCopied] = useState(false);
	const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { isUnlocked, decrypt } = useSecureUnlock();

	useEffect(() => () => {
		if (copiedTimer.current) clearTimeout(copiedTimer.current);
	}, []);

	if (!value) {
		return <Text style={[styles.text, styles.placeholder]}>{emptyPlaceholder}</Text>;
	}

	const encrypted = isEncryptedBlob(value);
	const serverMasked = !encrypted && /[*•]/.test(value);
	if (serverMasked) {
		return <Text selectable style={styles.text}>{value}</Text>;
	}

	const defaultMask = mask?.trim() || (encrypted ? '••••' : maskPlaintext(value));

	// The actual plaintext we can show/copy once revealed: decrypted blob, or the
	// stored value when it's legacy plaintext. `null` when locked / undecryptable.
	let revealedPlain: string | null = null;
	if (encrypted) {
		if (isUnlocked) {
			try { revealedPlain = decrypt(value); } catch { revealedPlain = null; }
		}
	} else {
		revealedPlain = value;
	}

	const showingPlain = revealed && revealedPlain != null;
	const display = showingPlain ? (revealedPlain as string) : defaultMask;
	const copyable = showingPlain && canCopy();

	const doCopy = async () => {
		if (revealedPlain == null) return;
		const ok = await writeClipboard(revealedPlain);
		if (!ok) return;
		setCopied(true);
		if (copiedTimer.current) clearTimeout(copiedTimer.current);
		copiedTimer.current = setTimeout(() => setCopied(false), 1500);
	};

	const handlePress = () => {
		// Masked → reveal (prompt unlock first when encrypted + locked).
		if (!revealed) {
			if (encrypted && !isUnlocked) {
				setUnlockOpen(true);
				return;
			}
			setRevealed(true);
			return;
		}
		// Revealed: copy the plaintext where supported; otherwise toggle back to masked.
		if (copyable) {
			void doCopy();
			return;
		}
		setRevealed(false);
	};

	const hoverIn = () => {
		if (encrypted && !isUnlocked) return;
		setRevealed(true);
	};
	const hoverOut = () => setRevealed(false);

	let icon: keyof typeof Ionicons.glyphMap = 'eye-outline';
	if (copied) icon = 'checkmark-outline';
	else if (copyable) icon = 'copy-outline';
	else if (revealed) icon = 'eye-off-outline';
	const iconColor = copied ? '#2e7d32' : '#78909c';

	let accessibilityLabel = 'Reveal Tax ID';
	if (copyable) accessibilityLabel = 'Copy to clipboard';
	else if (revealed) accessibilityLabel = 'Hide Tax ID';

	return (
		<>
			<Pressable
				onPress={handlePress}
				onHoverIn={hoverIn}
				onHoverOut={hoverOut}
				accessibilityLabel={accessibilityLabel}
				accessibilityRole="button"
				hitSlop={4}
			>
				<View style={styles.row}>
					<Text selectable numberOfLines={1} style={styles.text}>{display}</Text>
					<Ionicons name={icon} size={14} color={iconColor} />
				</View>
			</Pressable>
			<SecureUnlockModal
				visible={unlockOpen}
				onDismiss={() => setUnlockOpen(false)}
				message="Unlock to reveal this Tax ID."
				onUnlocked={() => { setUnlockOpen(false); setRevealed(true); }}
			/>
		</>
	);
};

const styles = StyleSheet.create({
	row: { flexDirection: 'row', alignItems: 'center', gap: 6 },
	text: { fontFamily: MONO_FONT },
	placeholder: { color: '#78909c' },
});
